import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import {
  ProvisionalContent,
  ProvisionalPost,
  ChannelAccount,
  ChannelConstraints,
  ProvisionalBody,
  ChannelIdentifier,
  Channel,
  PostOnChannel,
} from "../types";

type Poc = ProvisionalPost["post_on_channels_attributes"][0];

type PostOnChannelAttributes = ProvisionalPost["post_on_channels_attributes"][0];
type ErrorIdentifier =
  | "character_limit"
  | "video_count"
  | "embedded_link_count"
  | "manual_parameters"
  | "tiktok_settings_validation"
  | "youtube_settings_validation"
  | "date_missing"
  | "invalid_link";

// format final :
export type PubError = {
  affectedTargets: ProvisionalPost["post_on_channels_attributes"];
  actions: { label: string; callback: (arg: any) => void }[];
  message: string;
  inactionStrategyMessage: string;
  errorIdentifier: ErrorIdentifier;
  shouldBe: number | boolean;
};

type ConcatenedKey = `${ChannelIdentifier | "global"}|${ErrorIdentifier}`;

export type ValidationObject = {
  errors: { [K in ConcatenedKey]?: PubError };
  reverseMap: Record<number, ConcatenedKey[]>;
  hasNewErrors: boolean;
};

const usePubValidation = ({
  pocs,
  bcgs,
  instantPublish = true,
  hChannelAccounts = {},
  hBodies,
  setOpenModalForPoc,
  createNewBodyForGroup,
  deleteGroup,
  channelConstraints,
}: {
  pocs?: Poc[];
  bcgs?: ProvisionalContent["body_channel_groups_attributes"];
  channelConstraints?: Record<ChannelIdentifier, ChannelConstraints>;
  instantPublish?: boolean;
  hChannelAccounts?: Record<number, ChannelAccount>;
  hBodies: Record<number, ProvisionalBody>;
  setOpenModalForPoc?: (poc: Poc) => void;
  createNewBodyForGroup?: (targets: any[], err: string) => void;
  deleteGroup?: (targets: any[]) => void;
}): ValidationObject => {
  const intl = useIntl();
  const [errors, setErrors] = useState<ValidationObject["errors"]>({});
  const [reverseMap, setReverseMap] = useState<ValidationObject["reverseMap"]>({});
  const [hasNewErrors, setHasNewErrors] = useState(false);

  const addError = (
    errorList: ValidationObject["errors"],
    reverseList: ValidationObject["reverseMap"],
    chan: ChannelIdentifier | "global",
    errIdentifier: ErrorIdentifier,
    error: PubError,
    poc: PostOnChannelAttributes | null,
  ) => {
    const fullIdentifier = [chan, errIdentifier].join("|") as ConcatenedKey;
    const currentError = errorList[fullIdentifier] || error;
    const affectedTargets = currentError.affectedTargets;
    if (poc) {
      affectedTargets.push(poc);
      const currentKeys = reverseList[poc.channel_account_id] || [];
      if (!currentKeys.includes(fullIdentifier)) {
        reverseList[poc.channel_account_id] = currentKeys.concat(fullIdentifier);
      }
    }
    errorList[fullIdentifier] = { ...currentError, affectedTargets };
  };

  const validationRules: ValidationRules = {
    character_limit: {
      evaluation: (s, _p, b) => Boolean(!b.message || s - b.message.length >= 0),
      getMessage: (channel, limit) =>
        intl.formatMessage(
          {
            id: "pub_validation.character_limit",
            defaultMessage: "{channel} limite le texte à {limit} caractères",
          },
          { channel, limit },
        ),
      getInactionMessage: (limit) =>
        intl.formatMessage({
          id: "pub_validation.character_limit_inaction",
          defaultMessage: "Sans action de votre part, le message va être tronqué",
        }),
      actions: [
        {
          label: intl.formatMessage({
            id: "pub_validation.fix",
            defaultMessage: "corriger",
          }),
          callback: (pocs) => createNewBodyForGroup?.(pocs, "character_limit"),
        },
      ],
    },
  };

  const buildConstraintsErrors = ({
    reverseList,
    errorList,
    constraints,
    validationRules,
    poc,
    body,
    channelIdentifier,
    channelName,
  }: {
    errorList: ValidationObject["errors"];
    reverseList: ValidationObject["reverseMap"];
    constraints: ChannelConstraints;
    validationRules: ValidationRules;
    poc: Poc | null;
    body: ProvisionalBody;
    channelIdentifier: ChannelIdentifier;
    channelName?: string;
  }) => {
    Object.entries(constraints).forEach(([constraint, shouldBe]: any) => {
      const validationRule = validationRules[constraint as keyof ChannelConstraints];

      if (validationRule) {
        const { evaluation, getMessage, getInactionMessage, actions } = validationRule;

        if (!evaluation(shouldBe, poc, body)) {
          const message = getMessage(channelName || "", shouldBe || "");
          const inactionStrategyMessage = getInactionMessage(shouldBe || "");

          const error = {
            inactionStrategyMessage,
            message,
            actions,
            affectedTargets: [],
            shouldBe,
            errorIdentifier: constraint,
          };

          addError(errorList, reverseList, channelIdentifier, constraint, error, poc);
        }
      }
    });
  };

  useEffect(() => {
    const errorList = {};
    const reverseList = {};
    if (pocs) {
      pocs?.map((poc) => {
        const channelAccount = hChannelAccounts[poc.channel_account_id];
        const body = hBodies[poc.body_provisional_id] as ProvisionalBody;
        const constraints = channelAccount?.channel_constraints;
        const channelIdentifier = channelAccount?.channel?.identifier;

        if (!constraints || !body || !channelIdentifier) {
          return [];
        }

        buildConstraintsErrors({
          constraints,
          validationRules,
          poc,
          body,
          channelIdentifier,
          channelName: channelAccount.channel.name,
          errorList,
          reverseList,
        });

        if (channelIdentifier === "youtube" && !poc?.channel_specific_data?.video_title) {
          addError(
            errorList,
            reverseList,
            channelIdentifier,
            "youtube_settings_validation",
            {
              inactionStrategyMessage: intl.formatMessage({
                id: "pub_validation.youtube_title_inaction",
                defaultMessage: "Sans action de votre part, le titre sera généré automatiquement",
              }),
              message: intl.formatMessage({
                id: "pub_validation.youtube_title",
                defaultMessage: "YouTube : veuillez renseigner un titre pour la vidéo",
              }),
              actions: [
                {
                  callback: (pocs) => {
                    if (pocs[0]) {
                      setOpenModalForPoc?.(pocs[0]);
                    }
                  },
                  label: intl.formatMessage({
                    id: "pub_validation.edit",
                    defaultMessage: "modifier",
                  }),
                },
              ],
              affectedTargets: [],
              shouldBe: true,
              errorIdentifier: "youtube_settings_validation",
            },
            poc,
          );
        }

        if (channelIdentifier === "tiktok" && !poc?.channel_specific_data?.privacy_level) {
          addError(
            errorList,
            reverseList,
            channelIdentifier,
            "tiktok_settings_validation",
            {
              inactionStrategyMessage: intl.formatMessage({
                id: "pub_validation.tiktok_settings_inaction",
                defaultMessage: "Sans action de votre part, le post ne sera pas publié",
              }),
              message: intl.formatMessage({
                id: "pub_validation.tiktok_settings",
                defaultMessage: "TikTok nécessite une validation manuelle des paramètres",
              }),
              actions: [
                {
                  callback: (pocs) => {
                    if (pocs[0]) {
                      setOpenModalForPoc?.(pocs[0]);
                    }
                  },
                  label: intl.formatMessage({
                    id: "pub_validation.view",
                    defaultMessage: "voir",
                  }),
                },
              ],
              affectedTargets: [],
              shouldBe: true,
              errorIdentifier: "tiktok_settings_validation",
            },
            poc,
          );
        }

        if (!poc.forced_scheduled_at && !instantPublish) {
          addError(
            errorList,
            reverseList,
            "global",
            "date_missing",
            {
              inactionStrategyMessage: intl.formatMessage({
                id: "pub_validation.date_missing_inaction",
                defaultMessage: "Sans action de votre part, ils ne seront pas publiés",
              }),
              message: intl.formatMessage({
                id: "pub_validation.date_missing",
                defaultMessage: "Certains posts n'ont pas de date",
              }),
              actions: [
                {
                  callback: () => setOpenModalForPoc?.(poc),
                  label: intl.formatMessage({
                    id: "pub_validation.view",
                    defaultMessage: "voir",
                  }),
                },
              ],
              affectedTargets: [],
              shouldBe: true,
              errorIdentifier: "date_missing",
            },
            poc,
          );
        }
        const urlWithVariables =
          /^\b(https?:\/\/[^\s\/$.?#]*\$[A-Z]*[^\s]*)\b$|^([^\s\/$.?#]*\$[A-Z]*[^\s]*)$|\b^(https?:\/\/[^\s\/$.?#]*[^\s]*)(?:\/)?$|^tel:\+?[\d\s]+$/;
        if (body.link_url && !urlWithVariables.test(body.link_url as string)) {
          addError(
            errorList,
            reverseList,
            "global",
            "invalid_link",
            {
              message: intl.formatMessage({
                id: "pub_validation.invalid_link",
                defaultMessage: "Le format du lien n'est pas valide",
              }),
              inactionStrategyMessage: intl.formatMessage({
                id: "pub_validation.invalid_link_inaction",
                defaultMessage: "Le lien ne sera pas affiché",
              }),
              actions: [
                {
                  label: intl.formatMessage({
                    id: "pub_validation.remove",
                    defaultMessage: "supprimer",
                  }),
                  callback: (pocs) => createNewBodyForGroup?.(pocs, "invalid_link"),
                },
              ],
              affectedTargets: [],
              shouldBe: 1, //HACK: actuellement true empêche l'affichage général
              errorIdentifier: "date_missing",
            },
            poc,
          );
        }
      });
    } else if (bcgs) {
      bcgs?.map((bcg) => {
        const body = hBodies[bcg.body_provisional_id] as ProvisionalBody;
        bcg.channels.map((channel) => {
          const constraints = channelConstraints?.[channel];
          const channelIdentifier = channel;

          if (!constraints || !body || !channelIdentifier) {
            return;
          }

          buildConstraintsErrors({
            constraints,
            validationRules,
            poc: null,
            body,
            channelIdentifier,
            channelName: channel,
            errorList,
            reverseList,
          });
        });
      });
    }
    const newErrorCount = Object.keys(errorList).length;
    if (newErrorCount > Object.keys(errors).length) {
      setHasNewErrors(true);
    }
    setErrors(errorList);
    setReverseMap(reverseList);
  }, [
    pocs,
    bcgs,
    instantPublish,
    // hChannelAccounts,
    hBodies,
  ]);

  useEffect(() => {
    if (hasNewErrors) {
      setTimeout(() => setHasNewErrors(false), 1000); // Reset animation after 1 second
    }
  }, [hasNewErrors]);

  return { errors, reverseMap, hasNewErrors };
};

type EvaluationFn<C extends keyof ChannelConstraints> = (
  shouldBe: ChannelConstraints[C],
  poc: ProvisionalPost["post_on_channels_attributes"] | null,
  body: ProvisionalBody,
) => boolean;

type ValidationRules = {
  [K in keyof ChannelConstraints]?: {
    evaluation: EvaluationFn<K>;
    getMessage: (channel: string, limit: any) => string;
    getInactionMessage: (limit: any) => string;
    actions: { label: string; callback: (arg: any) => void }[];
  };
};

export default usePubValidation;
